import React, { useState, useEffect } from 'react'
import RepresentMoney from '../../utility/represent-money'
import sortBy from 'lodash/sortBy'
import orderBy from 'lodash/sortBy'

const RaportsTable = ({ raportData }) => {
  const { type: raportType, data, view, include_organizer } = raportData
  const table =
    raportType === 'debt'
      ? debtTable(data)
      : salesTable(data, { view, includeOrganizer: include_organizer })

  return table
}

const pricingModelLabel = (row, { short = false } = {}) => {
  if (short) {
    if (row.pricing_model === 'LEGACY_RESALE') {
      return 'Vanha hinnoittelu'
    }

    if (row.pricing_model === 'ORGANIZER_5_BUYER_FEE_10') {
      return 'Uusi hinnoittelu'
    }

    if (row.pricing_model === 'ORGANIZER_5_ONLY') {
      return 'Uusi ilman ostajan maksua'
    }
  }

  if (row.pricing_model_label) {
    return row.pricing_model_label
  }

  if (row.pricing_model === 'LEGACY_RESALE') {
    return 'Vanha hinnoittelu (5 % myyjän palvelumaksu)'
  }

  if (row.pricing_model === 'ORGANIZER_5_BUYER_FEE_10') {
    return 'Uusi hinnoittelu (10 % ostajan palvelumaksu)'
  }

  if (row.pricing_model === 'ORGANIZER_5_ONLY') {
    return 'Uusi hinnoittelu ilman ostajan palvelumaksua (korjattu)'
  }

  if (row.pricing_models?.length) {
    return row.pricing_models
      .map(model =>
        model === 'LEGACY_RESALE'
          ? 'Vanha hinnoittelu (5 % myyjän palvelumaksu)'
          : model === 'ORGANIZER_5_BUYER_FEE_10'
            ? 'Uusi hinnoittelu (10 % ostajan palvelumaksu)'
            : model
      )
      .join(', ')
  }

  return row.pricing_model || '—'
}

const salesRowClassName = pricingModel => {
  if (pricingModel === 'LEGACY_RESALE') {
    return 'sales-row--legacy'
  }

  if (pricingModel) {
    return 'sales-row--new'
  }

  return ''
}

const showMoney = amount => {
  if (!amount) {
    return '—'
  }

  return <RepresentMoney sum={amount} eurSuffix />
}

const FeeStack = ({ exVat, vat, inclVat }) => {
  if (!exVat && !vat && !inclVat) {
    return <span className="sales-fee-empty">—</span>
  }

  return (
    <div className="sales-fee-stack">
      <div className="sales-fee-stack__line">{showMoney(exVat)}</div>
      <div className="sales-fee-stack__line">{showMoney(vat)}</div>
      <div className="sales-fee-stack__line sales-fee-stack__line--total">
        {showMoney(inclVat)}
      </div>
    </div>
  )
}

const sumRows = (data, field) =>
  data.reduce((acc, row) => acc + (row[field] || 0), 0)

const getResolvedPricingModel = row =>
  row.pricing_model ||
  (row.pricing_models?.length === 1 ? row.pricing_models[0] : undefined)

const getSalesFeeAmounts = row => {
  const organizerGross =
    row.organizer_commission_amount ??
    (row.organizer_amount_wo_vat || 0) + (row.organizer_commission_vat_amount || 0)

  return {
    organizer_commission_ex_vat_amount: row.organizer_amount_wo_vat ?? 0,
    organizer_commission_vat_amount: row.organizer_commission_vat_amount ?? 0,
    organizer_commission_amount: organizerGross,
    seller_tiketti_fee_ex_vat_amount: row.seller_tiketti_fee_ex_vat_amount ?? 0,
    seller_tiketti_fee_vat_amount: row.seller_tiketti_fee_vat_amount ?? 0,
    seller_tiketti_fee_amount: row.seller_tiketti_fee_amount ?? 0,
    buyer_service_fee_ex_vat_amount: row.buyer_service_fee_ex_vat_amount ?? 0,
    buyer_service_fee_vat_amount: row.buyer_service_fee_vat_amount ?? 0,
    buyer_service_fee_amount: row.buyer_service_fee_amount ?? 0,
  }
}

const sumOrganizerGross = data =>
  data.reduce(
    (acc, row) => acc + (getSalesFeeAmounts(row).organizer_commission_amount || 0),
    0,
  )

const sumSalesFeeAmounts = (data, field) =>
  data.reduce((acc, row) => acc + (getSalesFeeAmounts(row)[field] || 0), 0)

const getExpectedResaleFromParts = data =>
  sumRows(data, 'seller_amount') +
  sumOrganizerGross(data) +
  sumSalesFeeAmounts(data, 'seller_tiketti_fee_amount')

const getTikettiAmountWithVat = (row, totalField) => {
  if (typeof row.tiketti_amount_w_vat === 'number') {
    return row.tiketti_amount_w_vat
  }

  if (typeof row.tiketti_fee_amount === 'number') {
    return row.tiketti_fee_amount
  }

  return (row[totalField] || 0) * 0.05
}

const salesNameHeader = view => {
  if (view === 'sellers') {
    return 'Myyjä'
  }

  if (view === 'months') {
    return 'Jakso'
  }

  if (view === 'summary') {
    return 'Yhteenveto'
  }

  return 'Tapahtuma'
}

const FINNISH_MONTH_NUMBERS = {
  tammikuu: 1,
  helmikuu: 2,
  maaliskuu: 3,
  huhtikuu: 4,
  toukokuu: 5,
  kesäkuu: 6,
  heinäkuu: 7,
  elokuu: 8,
  syyskuu: 9,
  lokakuu: 10,
  marraskuu: 11,
  joulukuu: 12
}

const periodSortKey = label => {
  const match = String(label || '')
    .trim()
    .match(/^(\S+)\s+(\d{4})$/)

  if (!match) {
    return null
  }

  const month = FINNISH_MONTH_NUMBERS[match[1].toLowerCase()]
  const year = Number(match[2])
  if (!month || !Number.isInteger(year)) {
    return null
  }

  return year * 100 + month
}

const compareText = (left, right) => {
  const a = left || ''
  const b = right || ''
  if (a < b) {
    return -1
  }
  if (a > b) {
    return 1
  }
  return 0
}

export const salesReportRowKey = (row, index) =>
  [
    index,
    row.report_row_key || '',
    row.organizer || '',
    row.event || '',
    row.pricing_model || ''
  ].join('|')

export const sortSalesReportRows = (data, view) => {
  if (view !== 'months') {
    return sortBy(data, ['organizer', 'event', 'pricing_model'])
  }

  return [...data].sort((a, b) => {
    const byOrganizer = compareText(a.organizer, b.organizer)
    if (byOrganizer !== 0) {
      return byOrganizer
    }

    const aPeriod = periodSortKey(a.event)
    const bPeriod = periodSortKey(b.event)
    if (aPeriod !== null && bPeriod !== null && aPeriod !== bPeriod) {
      return aPeriod - bPeriod
    }
    if (aPeriod !== null && bPeriod === null) {
      return -1
    }
    if (aPeriod === null && bPeriod !== null) {
      return 1
    }

    const byEvent = compareText(a.event, b.event)
    if (byEvent !== 0) {
      return byEvent
    }

    return compareText(a.pricing_model, b.pricing_model)
  })
}

const salesTable = (data, { view = 'events', includeOrganizer = false } = {}) => {
  const rows = sortSalesReportRows(data, view)
  const reconciliationGap =
    sumRows(rows, 'total_sold_amount') - getExpectedResaleFromParts(rows)
  const showOrganizer =
    Boolean(includeOrganizer) || view === 'events' || !view

  return (
    <section className="admin-v2__panel">
      <div className="admin-v2__panel-head">
        <div>
          <h2>Myyntiraportti</h2>
          <p className="admin-v2__panel-meta">
            Näkymä: {salesNameHeader(view)} · {data.length} riviä
          </p>
        </div>
      </div>
      <div className="admin-v2__panel-body">
        <div className="raport-table--legend">
          <p>
            <strong>Miten lukea raporttia:</strong> sama tapahtuma voi näkyä
            kahdella rivillä, jos myyntiä on ollut sekä vanhalla että uudella
            hinnoittelulla.
          </p>
          <ul>
            <li>
              <strong>Järjestäjän provisio</strong> ja <strong>Tiketin provisio</strong>{' '}
              (vanha malli) sekä <strong>ostajan palvelumaksu</strong> (uusi malli)
              näytetään ALV-erittelynä: veroton / ALV / sis. alv.
            </li>
            <li>
              ALV-kanta myynnin ajankohdan mukaan: 24 % (1.1.2013–31.8.2024),
              25,5 % (1.9.2024–).
            </li>
            <li>
              <span className="sales-row--legacy sales-row--legend-swatch" />
              <strong>Vanha hinnoittelu:</strong> ostaja maksaa jälleenmyyntihinnan.
              Myyjältä vähennetään Tiketin 5 % provisio.
            </li>
            <li>
              <span className="sales-row--new sales-row--legend-swatch" />
              <strong>Uusi hinnoittelu:</strong> ostajalle lisätään 10 % palvelumaksu.
            </li>
            <li>
              <strong>Tarkistus:</strong> Jälleenmyynti = Myyjälle + Järjestäjän
              provisio + Tiketin provisio (sis. alv -rivit). Ostaja = Jälleenmyynti
              + Ostajan palvelumaksu (uusi malli).
            </li>
          </ul>
          {Math.abs(reconciliationGap) > 0 && (
            <p className="raport-table--reconciliation-warning">
              Huom: rivien yhteenlaskussa on {showMoney(Math.abs(reconciliationGap))}{' '}
              heitto. Tämä johtuu yleensä vanhasta pyöristyslaskennasta ennen
              korjausta — päivitä raportti uudelleen tuotantokorjauksen jälkeen.
            </p>
          )}
        </div>

        <div className="table-wrap table-wrap--dense">
          <table id="salesTable" className="sales-report-table">
            <colgroup>
              {showOrganizer && <col className="sales-col--organizer" />}
              <col className="sales-col--event" />
              <col className="sales-col--model" />
              <col className="sales-col--count" />
              <col className="sales-col--money" span="3" />
              <col className="sales-col--fee" span="3" />
            </colgroup>
            <thead>
              <tr>
                {showOrganizer && (
                  <th title="Järjestäjä">
                    {view === 'sellers' ? 'Sähköposti' : 'Järjestäjä'}
                  </th>
                )}
                <th title={salesNameHeader(view)}>{salesNameHeader(view)}</th>
                <th title="Hinnoittelumalli">Malli</th>
                <th title="Myynti kpl">kpl</th>
                <th title="Jälleenmyyntihinta yhteensä">Jälleenmyynti</th>
                <th title="Ostaja maksoi yhteensä">Ostaja</th>
                <th title="Myyjälle">Myyjälle</th>
                <th title="Järjestäjän 5 % provisio (sis. ALV-erittely)">
                  Järjestäjän provisio
                  <span className="sales-fee-stack-label">vt / alv / sis</span>
                </th>
                <th title="Tiketin 5 % provisio vanhalla hinnoittelulla (vähennetään myyjältä)">
                  Tiketin provisio
                  <span className="sales-fee-stack-label">vt / alv / sis</span>
                </th>
                <th title="Ostajan 10 % palvelumaksu uudella hinnoittelulla">
                  Ostajan palvelumaksu
                  <span className="sales-fee-stack-label">vt / alv / sis</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {generateSalesTableRows(rows, { showOrganizer })}
              {generateSalesSummaryRow(rows, { showOrganizer })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

const generateSalesTableRows = (data, { showOrganizer = false } = {}) =>
  data.map((row, index) => {
    const {
      event,
      organizer,
      tickets_sold,
      total_sold_amount,
      buyer_total_amount,
      seller_amount
    } = row

    const resolvedPricingModel = getResolvedPricingModel(row)
    const fees = getSalesFeeAmounts(row)

    return (
      <tr
        key={salesReportRowKey(row, index)}
        className={salesRowClassName(resolvedPricingModel)}
      >
        {showOrganizer && <td>{organizer}</td>}
        <td className="sales-event">
          <span className="cellname">
            <span
              className={`dot ${
                resolvedPricingModel === 'LEGACY_RESALE'
                  ? 'dot--vanha'
                  : 'dot--uusi'
              }`}
            />
            <span>{event}</span>
          </span>
        </td>
        <td className="sales-pricing-model" title={pricingModelLabel(row)}>
          {pricingModelLabel(row, { short: true })}
        </td>
        <td className="numeric">{tickets_sold}</td>
        <td className="numeric sales-money">{showMoney(total_sold_amount)}</td>
        <td className="numeric sales-money">{showMoney(buyer_total_amount)}</td>
        <td className="numeric sales-money">{showMoney(seller_amount)}</td>
        <td className="numeric">
          <FeeStack
            exVat={fees.organizer_commission_ex_vat_amount}
            vat={fees.organizer_commission_vat_amount}
            inclVat={fees.organizer_commission_amount}
          />
        </td>
        <td className="numeric">
          <FeeStack
            exVat={fees.seller_tiketti_fee_ex_vat_amount}
            vat={fees.seller_tiketti_fee_vat_amount}
            inclVat={fees.seller_tiketti_fee_amount}
          />
        </td>
        <td className="numeric">
          <FeeStack
            exVat={fees.buyer_service_fee_ex_vat_amount}
            vat={fees.buyer_service_fee_vat_amount}
            inclVat={fees.buyer_service_fee_amount}
          />
        </td>
      </tr>
    )
  })

const generateSalesSummaryRow = (data, { showOrganizer = false } = {}) => {
  const pricingModels = [
    ...new Set(data.map(getResolvedPricingModel).filter(Boolean))
  ]

  return (
    <tr className="sales-row--summary">
      {showOrganizer && <th />}
      <th>Yhteensä</th>
      <th>{pricingModels.length > 1 ? 'Useita malleja' : pricingModelLabel(data[0] || {}, { short: true })}</th>
      <th className="numeric">{sumRows(data, 'tickets_sold')}</th>
      <td className="numeric sales-money">{showMoney(sumRows(data, 'total_sold_amount'))}</td>
      <td className="numeric sales-money">{showMoney(sumRows(data, 'buyer_total_amount'))}</td>
      <td className="numeric sales-money">{showMoney(sumRows(data, 'seller_amount'))}</td>
      <td className="numeric">
        <FeeStack
          exVat={sumSalesFeeAmounts(data, 'organizer_commission_ex_vat_amount')}
          vat={sumSalesFeeAmounts(data, 'organizer_commission_vat_amount')}
          inclVat={sumOrganizerGross(data)}
        />
      </td>
      <td className="numeric">
        <FeeStack
          exVat={sumSalesFeeAmounts(data, 'seller_tiketti_fee_ex_vat_amount')}
          vat={sumSalesFeeAmounts(data, 'seller_tiketti_fee_vat_amount')}
          inclVat={sumSalesFeeAmounts(data, 'seller_tiketti_fee_amount')}
        />
      </td>
      <td className="numeric">
        <FeeStack
          exVat={sumSalesFeeAmounts(data, 'buyer_service_fee_ex_vat_amount')}
          vat={sumSalesFeeAmounts(data, 'buyer_service_fee_vat_amount')}
          inclVat={sumSalesFeeAmounts(data, 'buyer_service_fee_amount')}
        />
      </td>
    </tr>
  )
}

const debtTable = data => {
  return (
    <div className="raport-table--container">
      <RaportHider topic={'Asiakkaat'} data={generateTop(data.top)} />

      <div className="break-lg" />

      <RaportHider topic={'Järjestäjät'} data={generateMiddle(data.middle)} />

      <div className="break-lg" />

      <RaportHider topic={'Aikaväli'} data={generateBottom(data.bottom)} />
    </div>
  )
}

const generateTop = data => {
  const userSortedData = sortBy(data, ['name'])

  const rows = userSortedData.map(row => {
    const {
      name,
      tickets_sold,
      total_not_sepaed_user,
      total_sepaed_user,
      total_not_sepaed_org,
      total_sepaed_org,
      total_sold_amount
    } = row

    return (
      <tr key={'debt-' + name}>
        <td>{name}</td>
        <td>{<RepresentMoney sum={total_sold_amount} eurSuffix />}</td>
        <td>{tickets_sold}</td>
        <td>{<RepresentMoney sum={total_sepaed_user} eurSuffix />}</td>
        <td>{<RepresentMoney sum={total_sepaed_org} eurSuffix />}</td>
        <td>
          {
            <RepresentMoney
              sum={total_sepaed_user + total_sepaed_org}
              eurSuffix
            />
          }
        </td>
        <td>{<RepresentMoney sum={total_not_sepaed_user} eurSuffix />}</td>
        <td>{<RepresentMoney sum={total_not_sepaed_org} eurSuffix />}</td>
        <td>
          {
            <RepresentMoney
              sum={total_not_sepaed_user + total_not_sepaed_org}
              eurSuffix
            />
          }
        </td>
        <td>
          {<RepresentMoney sum={getTikettiAmountWithVat(row, 'total_sold_amount')} eurSuffix />}
        </td>
      </tr>
    )
  })

  const topSummary = () => {
    const totalSold = data.reduce((a, b) => a + b.total_sold_amount, 0)
    const totalTicketsSold = data.reduce((a, b) => a + b.tickets_sold, 0)
    const totalSepaedCustomer = data.reduce(
      (a, b) => a + b.total_sepaed_user,
      0
    )
    const totalSepaedOrganizer = data.reduce(
      (a, b) => a + b.total_sepaed_org,
      0
    )
    const totalSepaedCustomerAndOrganizer = data.reduce(
      (a, b) => a + b.total_sepaed_user + b.total_sepaed_org,
      0
    )
    const totalNotSepaedCustomer = data.reduce(
      (a, b) => a + b.total_not_sepaed_user,
      0
    )
    const totalNotSepaedOrganizer = data.reduce(
      (a, b) => a + b.total_not_sepaed_org,
      0
    )
    const totalNotSepaedCustomerAndOrganizer = data.reduce(
      (a, b) => a + b.total_not_sepaed_org + b.total_not_sepaed_user,
      0
    )
    const totalSoldAmountForTiketti = data.reduce(
      (a, b) => a + getTikettiAmountWithVat(b, 'total_sold_amount'),
      0
    )

    return (
      <tr style={{ fontWeight: 'bold' }}>
        <td>Yhteensä</td>
        <td>{<RepresentMoney sum={totalSold} eurSuffix />}</td>
        <td>{totalTicketsSold}</td>
        <td>{<RepresentMoney sum={totalSepaedCustomer} eurSuffix />}</td>
        <td>{<RepresentMoney sum={totalSepaedOrganizer} eurSuffix />}</td>
        <td>
          {<RepresentMoney sum={totalSepaedCustomerAndOrganizer} eurSuffix />}
        </td>
        <td>{<RepresentMoney sum={totalNotSepaedCustomer} eurSuffix />}</td>
        <td>{<RepresentMoney sum={totalNotSepaedOrganizer} eurSuffix />}</td>
        <td>
          {
            <RepresentMoney
              sum={totalNotSepaedCustomerAndOrganizer}
              eurSuffix
            />
          }
        </td>
        <td>{<RepresentMoney sum={totalSoldAmountForTiketti} eurSuffix />}</td>
      </tr>
    )
  }

  return (
    <div className="table-wrap table-wrap--dense">
    <table id="salesTable">
      <thead>
        <tr>
          <th>Lipunmyyjä</th>
          <th colSpan="2">Kokonaismyynti</th>
          <th colSpan="3">Tilitetty</th>
          <th colSpan="3">Velka</th>
          <th>Tiketin provisio</th>
        </tr>

        <tr>
          <th>nimi</th>
          <th>eur</th>
          <th>kpl</th>
          <th>Lipunmyyjä</th>
          <th>Järjestäjä</th>
          <th>Tilitetty yhteensä</th>
          <th>Lipunmyyjä</th>
          <th>Järjestäjä</th>
          <th>Velka yhteensä</th>
        </tr>
      </thead>

      <tbody>
        {rows}
        {topSummary()}
      </tbody>
    </table>
    </div>
  )
}

const generateMiddle = data => {
  const sortedData = sortBy(data, ['organizer_name'])

  const rows = sortedData.map(row => {
    const { organizer_name, total_not_sepaed, total_sales, total_sepaed } = row

    return (
      <tr>
        <td>{organizer_name}</td>
        <td>
          <RepresentMoney sum={total_sales} eurSuffix />
        </td>
        <td>
          <RepresentMoney sum={total_sepaed} eurSuffix />
        </td>
        <td>
          <RepresentMoney sum={total_not_sepaed} eurSuffix />
        </td>
      </tr>
    )
  })

  const summary = () => {
    const totalSales = data.reduce((a, b) => a + b.total_sales, 0)
    const totalSepaed = data.reduce((a, b) => a + b.total_sepaed, 0)
    const totalNotSepaed = data.reduce((a, b) => a + b.total_not_sepaed, 0)

    return (
      <tr style={{ fontWeight: 'bold' }}>
        <td>Yhteensä</td>
        <td>{<RepresentMoney sum={totalSales} eurSuffix />}</td>
        <td>{<RepresentMoney sum={totalSepaed} eurSuffix />}</td>
        <td>{<RepresentMoney sum={totalNotSepaed} eurSuffix />}</td>
      </tr>
    )
  }

  return (
    <div className="table-wrap table-wrap--dense">
    <table
      id="salesTable"
      className="admin-panel-table"
    >
      <thead>
        <tr>
          <th>Järjestäjä</th>
          <th>Kokonaismyynti</th>
          <th>Tilitetty</th>
          <th>Velka</th>
        </tr>
      </thead>

      <tbody>
        {rows}
        {summary()}
      </tbody>
    </table>
    </div>
  )
}

const generateBottom = data => {
  const sortedData = orderBy(data, ['year_number', 'month_number'], ['asc'])

  const rows = sortedData.map(row => {
    const {
      basket_amount,
      month,
      total,
      total_not_sepaed_user,
      total_not_sepaed_org,
      total_sepaed_user,
      total_sepaed_org
    } = row

    return (
      <tr key={'debt-bottom' + month}>
        <td>{month}</td>
        <td>
          <RepresentMoney sum={total} eurSuffix />
        </td>
        <td>{basket_amount}</td>
        <td>
          <RepresentMoney sum={total_sepaed_user} eurSuffix />
        </td>
        <td>
          <RepresentMoney sum={total_sepaed_org} eurSuffix />
        </td>
        <td>
          <RepresentMoney
            sum={total_sepaed_org + total_sepaed_user}
            eurSuffix
          />
        </td>
        <td>
          <RepresentMoney sum={total_not_sepaed_user} eurSuffix />
        </td>
        <td>
          <RepresentMoney sum={total_not_sepaed_org} eurSuffix />
        </td>
        <td>
          <RepresentMoney
            sum={total_not_sepaed_org + total_not_sepaed_user}
            eurSuffix
          />
        </td>
        <td>
          <RepresentMoney sum={getTikettiAmountWithVat(row, 'total')} eurSuffix />
        </td>
      </tr>
    )
  })

  const summary = () => {
    const totalSold = data.reduce((a, b) => a + b.total, 0)
    const totalTicketsSold = data.reduce((a, b) => a + b.basket_amount, 0)
    const totalSepaedCustomer = data.reduce(
      (a, b) => a + b.total_sepaed_user,
      0
    )
    const totalSepaedOrganizer = data.reduce(
      (a, b) => a + b.total_sepaed_org,
      0
    )
    const totalSepaedCustomerAndOrganizer = data.reduce(
      (a, b) => a + b.total_sepaed_user + b.total_sepaed_org,
      0
    )
    const totalNotSepaedCustomer = data.reduce(
      (a, b) => a + b.total_not_sepaed_user,
      0
    )
    const totalNotSepaedOrganizer = data.reduce(
      (a, b) => a + b.total_not_sepaed_org,
      0
    )
    const totalNotSepaedCustomerAndOrganizer = data.reduce(
      (a, b) => a + b.total_not_sepaed_user + b.total_not_sepaed_org,
      0
    )
    const totalSoldAmountForTiketti = data.reduce(
      (a, b) => a + getTikettiAmountWithVat(b, 'total'),
      0
    )

    return (
      <tr style={{ fontWeight: 'bold' }}>
        <td>Yhteensä</td>
        <td>{<RepresentMoney sum={totalSold} eurSuffix />}</td>
        <td>{totalTicketsSold}</td>
        <td>{<RepresentMoney sum={totalSepaedCustomer} eurSuffix />}</td>
        <td>{<RepresentMoney sum={totalSepaedOrganizer} eurSuffix />}</td>
        <td>
          {<RepresentMoney sum={totalSepaedCustomerAndOrganizer} eurSuffix />}
        </td>
        <td>{<RepresentMoney sum={totalNotSepaedCustomer} eurSuffix />}</td>
        <td>{<RepresentMoney sum={totalNotSepaedOrganizer} eurSuffix />}</td>
        <td>
          {
            <RepresentMoney
              sum={totalNotSepaedCustomerAndOrganizer}
              eurSuffix
            />
          }
        </td>
        <td>{<RepresentMoney sum={totalSoldAmountForTiketti} eurSuffix />}</td>
      </tr>
    )
  }

  return (
    <div className="table-wrap table-wrap--dense">
    <table
      id="salesTable"
      className="admin-panel-table"
    >
      <thead>
        <tr>
          <th>Jakso</th>
          <th colSpan="2">Kokonaismyynti</th>
          <th colSpan="3">Tilitetty</th>
          <th colSpan="3">Velka</th>
          <th>Tikettii provisio</th>
        </tr>

        <tr>
          <th />
          <th>Eur</th>
          <th>Kpl</th>
          <th>Lipunmyyjä</th>
          <th>Järjestäjä</th>
          <th>Tilitetty yhteensä</th>
          <th>Lipunmyyjä</th>
          <th>Järjestäjä</th>
          <th>Velka yhteensä</th>
        </tr>
      </thead>

      <tbody>
        {rows}
        {summary()}
      </tbody>
    </table>
    </div>
  )
}

const RaportHider = ({ topic, data }) => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setOpen(false)
  }, [topic, data])

  return (
    <div className="row">
      <div className="col-sm-10">{topic}</div>
      <div className="col-sm-2">
        <button onClick={() => setOpen(!open)}>
          {open ? 'sulje' : 'avaa'}
        </button>
      </div>

      {open && data}
    </div>
  )
}

export default RaportsTable
